import { Component, Vue, Prop } from "vue-property-decorator";
import { CreateElement, VNode } from "vue/types/umd";
import { VContainer } from "vuetify/lib";
import SchemeIntroduction from "defi/components/partial/SchemeIntroduction.vue";
import DepositForm from "../normal/DepositForm.vue";
import { TermScheme, SchemeProgress } from "@foxone/api/src/models/defi";
import { RevenueTimeline } from "defi/components/partial/RevenueTimeline";

@Component
class TermContractJoin extends Vue {
  @Prop()
  scheme!: TermScheme;

  fullmode = false;

  get meta() {
    const duration = this.scheme?.duration ?? 0;

    let interestRatio = 0;
    if (this.scheme && "interest_ratio" in this.scheme) {
      interestRatio = Number(this.scheme.interest_ratio);
    }

    const symbol = this.scheme?.pay_asset.symbol;

    return {
      symbol,
      duration,
      interestRatio
    };
  }

  get timelineItems() {
    if (!this.scheme) {
      return [];
    }

    const formatDate = (t, p = "MM-DD") =>
      this.$common.utils.date.formatDate(t * 1000, p);

    const oneday = 24 * 3600;
    const today = new Date().getTime() / 1000;
    const raiseAt = this.scheme.interest_at || today + oneday;
    const expireAt = raiseAt + Number(this.scheme.duration) * oneday;
    const redemptionAt = expireAt + oneday;

    const from = this.scheme.from;
    const to = this.scheme.to;
    let buyText = this.$t("common.today");
    if (from || to) {
      buyText = formatDate(from) + " ~ " + formatDate(to);
    }

    return [
      {
        text: this.$t("defi.buy"),
        label: buyText
      },
      {
        text: this.$t("defi.start-revenue"),
        label: formatDate(raiseAt)
      },
      {
        text: this.$t("defi.onexpire"),
        label: formatDate(expireAt)
      },
      {
        text: this.$t("defi.redemption"),
        label: formatDate(redemptionAt)
      }
    ];
  }

  genExpectedYield(amount) {
    const h = this.$createElement;

    if (!amount) return null;

    const { symbol, duration, interestRatio } = this.meta;
    const expectedYield = (
      (duration * Number(amount) * interestRatio) /
      365
    ).toFixed(8);

    const expectedYieldText = this.$t("defi.expect-yield-tip", {
      amount: `${expectedYield} ${symbol}`
    }) as string;

    return h("f-tip", { staticClass: "my-5 rounded" }, [expectedYieldText]);
  }

  genDepositForm() {
    const h = this.$createElement;
    const { scheme } = this;
    const {
      id,
      progress,
      min_subscribe,
      max_subscribe,
      pay_asset,
      pay_asset_id
    } = scheme;
    const disabled = progress !== SchemeProgress.OPENING;
    const isFull = progress === SchemeProgress.FULL;
    const limits = {
      [pay_asset_id]: { min: +min_subscribe, max: +max_subscribe }
    };

    return [
      h(DepositForm, {
        props: {
          schemeId: id,
          limits,
          disabled,
          isFull,
          assets: [pay_asset]
        },
        scopedSlots: {
          formAppend: ({ amount }) => {
            return this.genExpectedYield(amount);
          }
        }
      })
    ];
  }

  render(h: CreateElement): VNode {
    return h(VContainer, [
      h(SchemeIntroduction, {
        props: { fullmode: this.fullmode, scheme: this.scheme }
      }),
      h("f-panel", { staticClass: "mt-3" }, [
        h(RevenueTimeline, {
          staticClass: "my-5",
          props: { items: this.timelineItems }
        }),
        this.genDepositForm()
      ])
    ]);
  }
}

export default TermContractJoin;
